import { Link, useNavigate, useLocation } from "react-router-dom";
import { Linkedin, Youtube } from "lucide-react";
import { LOGO_PNG, LOGO_GIF } from "../data/site";
import { FOOTER_SECTION } from "../data/home-data";

// New X (formerly Twitter) brand logo — lucide-react doesn't ship it.
const XIcon = ({ className }) => (
    <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        aria-hidden="true"
    >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

const SOCIALS = [
    { Icon: XIcon, href: "https://x.com/AIProfHQ", label: "x" },
    { Icon: Linkedin, href: "https://www.linkedin.com/company/aiprofhq/", label: "linkedin" },
    { Icon: Youtube, href: "https://youtube.com/@aiprofhq", label: "youtube" },
];

export default function Footer() {
    const navigate = useNavigate();
    const location = useLocation();

    const scrollToHash = (hash, attempt = 0) => {
        const el = document.getElementById(hash);
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
        } else if (attempt < 10) {
            // Page may still be rendering after navigation — retry briefly.
            setTimeout(() => scrollToHash(hash, attempt + 1), 100);
        }
    };

    const handleHashLink = (to) => {
        const [path, hash] = to.split("#");
        const onPage =
            !path ||
            location.pathname === path ||
            location.pathname === "/new" + path;
        if (onPage) {
            // Already on the right page — just scroll to the section.
            scrollToHash(hash);
        } else {
            // Navigate first, then locate the section only AFTER the new page
            // has mounted (delay avoids scrolling the old page's matching id).
            navigate(path);
            setTimeout(() => scrollToHash(hash), 200);
        }
    };
    return (
        <footer
            className="bg-[#0A0A0A] text-white pt-20 pb-10 relative overflow-hidden"
            data-testid="site-footer"
        >
            <div
                className="absolute inset-0 opacity-20"
                style={{
                    background:
                        "radial-gradient(circle at 80% 20%, #0052FF, transparent 60%)",
                }}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
                <div className="grid lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-10 pb-14">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <img
                                src={LOGO_PNG}
                                alt="AI Prof"
                                className="h-16 w-auto object-contain"
                            />
                        </div>
                        <p
                            className="text-white/70 max-w-sm leading-relaxed text-sm"
                            dangerouslySetInnerHTML={{
                                __html: FOOTER_SECTION.tagline,
                            }}
                        />
                        <div
                            className="flex gap-3 mt-6"
                            data-testid="footer-socials"
                        >
                            {SOCIALS.map(({ Icon, href, label }) => (
                                <a
                                    key={label}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="h-10 w-10 rounded-full bg-white/5 hover:bg-aiblue flex items-center justify-center transition"
                                    data-testid={`social-${label}`}
                                >
                                    <Icon className="h-4 w-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {FOOTER_SECTION.columns.map((col) => (
                        <div key={col.title}>
                            <h4 className="font-display font-bold text-sm uppercase tracking-widest mb-4 text-white/90">
                                {col.title}
                            </h4>
                            <ul className="space-y-2.5">
                                {col.links.map(([label, to]) => {
                                    const isMail = to.startsWith("mailto:");
                                    const isHash = !isMail && to.includes("#");
                                    if (isMail) {
                                        return (
                                            <li key={label}>
                                                <a
                                                    href={to}
                                                    className="text-sm text-white/60 hover:text-aiorange transition"
                                                >
                                                    {label}
                                                </a>
                                            </li>
                                        );
                                    }
                                    if (isHash) {
                                        return (
                                            <li key={label}>
                                                <button
                                                    onClick={() =>
                                                        handleHashLink(to)
                                                    }
                                                    className="text-sm text-white/60 hover:text-aiorange transition cursor-pointer bg-transparent border-none p-0 text-left"
                                                >
                                                    {label}
                                                </button>
                                            </li>
                                        );
                                    }
                                    return (
                                        <li key={label}>
                                            <Link
                                                to={to}
                                                className="text-sm text-white/60 hover:text-aiorange transition"
                                                data-testid={`footer-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                            >
                                                {label}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}

                    <div>
                        <h4 className="font-display font-bold text-sm uppercase tracking-widest mb-4 text-white/90">
                            {FOOTER_SECTION.getInTouch}
                        </h4>
                        <a
                            href={`mailto:${FOOTER_SECTION.email}`}
                            className="text-sm text-white/60 hover:text-aiorange block"
                            data-testid="footer-email"
                        >
                            {FOOTER_SECTION.email}
                        </a>
                        {FOOTER_SECTION.phone && (
                            <a
                                href={`tel:${FOOTER_SECTION.phone}`}
                                className="text-sm text-white/60 hover:text-aiorange block mt-1"
                                data-testid="footer-phone"
                            >
                                {FOOTER_SECTION.phone}
                            </a>
                        )}
                        <p className="text-sm text-white/60 mt-2">
                            {FOOTER_SECTION.location}
                        </p>
                    </div>
                </div>

                {/* big footer logo */}
                <div className="border-t border-white/10 py-10 flex flex-col items-center">
                    <img
                        src={LOGO_GIF}
                        alt="AI Prof"
                        className="h-40 w-auto object-contain"
                    />
                </div>

                <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
                    <span>{FOOTER_SECTION.copyright}</span>
                    <div className="flex gap-4">
                        <Link to="/legal/privacy">Privacy</Link>
                        <Link to="/legal/cookies">Cookies</Link>
                        <Link to="/legal/terms">Terms</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
